// PotreeViewer3 리팩토링 - Potree 뷰어 초기화 + 포인트클라우드 로딩 훅
// 역할:
// - window.Potree.Viewer 생성 (단일 출처)
// - 포인트클라우드 로딩 및 InitialSetting (fitToScreen, 카메라 초기 각도)
// - viewer 준비 시 Zustand(usePotreeStore)에 1회만 set
// - cleanup 시 viewer dispose 하지 않고 참조만 정리

import { useEffect, useState } from "react";
import { getTileset } from "../api/ProjectAPI";
import { usePotreeStore } from "../Store/PotreeStore";

/**
 * usePotreeInit
 *
 * @param {{ containerRef: React.RefObject, fileId: string }} options
 * @returns {{ isLoaded: boolean }}
 */
export function usePotreeInit({ containerRef, fileId }) {
  const [isLoaded, setIsLoaded] = useState(true); // true: 아직 뷰어/포인트클라우드 미완료, false: 뷰어 생성됨(로딩 중), 이후 InitialSetting에서 true로
  const setPotreeViewer = usePotreeStore((s) => s.setPotreeViewer);

  // 1) LoadPotree - 뷰어 생성 및 store 등록
  useEffect(() => {
    if (!window.Potree || !containerRef?.current) return;

    let viewer = null;
    try {
      viewer = new window.Potree.Viewer(containerRef.current);
      setPotreeViewer(viewer);
      setIsLoaded(false);
    } catch (err) {
      console.warn("Potree 뷰어 설정 오류:", err);
    }

    return () => {
      // viewer dispose 하지 않고 참조만 정리 (지침)
      setPotreeViewer(null);
    };
  }, []);

  // 2) LoadPointCloud + InitialSetting (viewer 준비 후, isLoaded === false 일 때 1회)
  useEffect(() => {
    const viewer = usePotreeStore.getState().potreeViewer;
    if (!viewer || isLoaded || !window.Potree || !fileId) return;

    const convertToAbsoluteUrl = (url, baseIP) => {
      if (baseIP === undefined) return url;
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      const baseUrl = baseIP || window.location.origin;
      return `${baseUrl}${url.startsWith("/") ? url : "/" + url}`;
    };

    const InitialSetting = (pointCloud) => {
      viewer.scene.addPointCloud(pointCloud);
      viewer.fitToScreen(0.1);
      setTimeout(() => {
        const view = viewer.scene.view;
        view.yaw = Math.PI / 3;
        view.pitch = -Math.PI / 2.5;
      }, 100);
      setIsLoaded(true);
    };

    const LoadPointCloud = async () => {
      try {
        const response = await getTileset(fileId, "potree");
        let url = response.baseUrl;
        const fileServerIP = process.env.REACT_APP_BASE_URL;
        url = convertToAbsoluteUrl(url, fileServerIP);
        const result = await window.Potree.loadPointCloud(url);
        const pointCloud = result.pointcloud;
        InitialSetting(pointCloud);
      } catch (err) {
        console.warn("포인트 클라우드 로딩 오류:", err);
      }
    };

    LoadPointCloud();
  }, [isLoaded, fileId]);

  return { isLoaded };
}
